import { useEffect, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { BBQ_DISHES } from "@/lib/bbqDishes";
import { waLink } from "@/lib/brand";
import { Reveal } from "@/lib/useReveal";

/**
 * BBQ Assador — Pratos em Destaque
 * Light sand background, premium grid, hover zoom, on-click lightbox.
 */
export const BBQDishes = () => {
  const [active, setActive] = useState(null); // index | null
  const message = "Olá! Gostaria de fazer uma reserva no BBQ Assador.";

  // Close on ESC + lock scroll when open
  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % BBQ_DISHES.length);
      if (e.key === "ArrowLeft")
        setActive((i) => (i - 1 + BBQ_DISHES.length) % BBQ_DISHES.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [active]);

  return (
    <section
      id="pratos-destaque"
      data-testid="bbq-dishes"
      className="relative bg-[#F5EDD9] text-[#1A1510] py-16 sm:py-20"
    >
      {/* top fade from dark */}
      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#1A0E08] to-transparent pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* HEADER — compact, lateral */}
        <Reveal variant="up" className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
              Capítulo 04 — A casa pede
            </span>
            <h2 className="mt-3 font-serif-bbq text-[#1A1510] text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[0.95]">
              Pratos em <em className="italic text-[#E85A1F]">destaque.</em>
            </h2>
          </div>
          <p className="font-gateway-sans text-[#6B5B43] text-sm sm:text-base leading-relaxed max-w-sm md:text-right">
            Os clássicos que fazem voltar.
          </p>
        </Reveal>

        {/* GRID */}
        <div className="mt-10 sm:mt-14 grid grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          {BBQ_DISHES.map((d, i) => (
            <Reveal
              key={d.slug}
              variant="up"
              delay={i * 90}
              className="contents"
            >
              <DishCard dish={d} onOpen={() => setActive(i)} />
            </Reveal>
          ))}
        </div>

        {/* CTA strip */}
        <Reveal
          variant="up"
          delay={200}
          className="mt-12 sm:mt-16 pt-8 border-t border-[#1A1510]/10 flex flex-col sm:flex-row items-center justify-between gap-6"
        >
          <p className="font-gateway-serif italic text-[#1A1510] text-xl sm:text-2xl leading-tight max-w-md text-center sm:text-left">
            Mais pratos em breve — fotografia real, sem filtros.
          </p>
          <a
            href={waLink(message)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bbq-dishes-reserve"
            className="btn btn-md btn-primary-bbq"
          >
            Reservar via WhatsApp
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
          </a>
        </Reveal>
      </div>

      {/* bottom fade back to dark */}
      <div className="h-16 mt-24 bg-gradient-to-b from-transparent to-[#1A0E08]" />

      {/* LIGHTBOX */}
      {active !== null && (
        <Lightbox
          dishes={BBQ_DISHES}
          index={active}
          onClose={() => setActive(null)}
          onPrev={() =>
            setActive((i) => (i - 1 + BBQ_DISHES.length) % BBQ_DISHES.length)
          }
          onNext={() => setActive((i) => (i + 1) % BBQ_DISHES.length)}
        />
      )}
    </section>
  );
};

/* ============ Card ============ */
const DishCard = ({ dish, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    data-testid={`bbq-dish-card-${dish.slug}`}
    aria-label={`Abrir foto de ${dish.name}`}
    className="group text-left outline-none focus-visible:ring-2 focus-visible:ring-[#E85A1F]/50 rounded-sm"
  >
    {/* Image */}
    <div className="relative aspect-[4/5] overflow-hidden bg-[#EADFBE] shadow-[0_20px_40px_-25px_rgba(26,21,16,0.4)] group-hover:shadow-[0_30px_55px_-25px_rgba(26,21,16,0.55)] transition-shadow duration-700">
      <img
        src={dish.img}
        alt={dish.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover img-enhance transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
      />
      {/* subtle warm vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1510]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      {/* hairline frame on hover */}
      <div className="absolute inset-0 ring-0 group-hover:ring-1 ring-[#1A1510]/15 transition-all duration-500" />
    </div>

    {/* Caption */}
    <div className="mt-5 sm:mt-6 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <h3 className="font-gateway-serif text-[#1A1510] text-xl sm:text-2xl leading-tight tracking-[-0.01em] transition-transform duration-500 group-hover:translate-x-0.5">
          {dish.name}
        </h3>
        <p className="mt-2 font-gateway-sans text-[13px] sm:text-sm text-[#6B5B43] leading-relaxed max-w-[34ch]">
          {dish.desc}
        </p>
      </div>
      {dish.price && (
        <div className="shrink-0 pt-1.5">
          <span className="font-gateway-serif italic text-[#1A1510] text-lg sm:text-xl tracking-tight">
            {dish.price}
            <span className="text-[#E85A1F] ml-0.5">€</span>
          </span>
        </div>
      )}
    </div>
  </button>
);

/* ============ Lightbox ============ */
const Lightbox = ({ dishes, index, onClose, onPrev, onNext }) => {
  const d = dishes[index];

  // Mobile swipe support
  const onTouchStart = (e) => {
    e.currentTarget._tx = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const start = e.currentTarget._tx;
    if (typeof start !== "number") return;
    const dx = e.changedTouches[0].clientX - start;
    if (dx > 50) onPrev();
    else if (dx < -50) onNext();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={d.name}
      data-testid="bbq-dish-lightbox"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-[#1A0E08]/92 backdrop-blur-md gateway-fade"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        data-testid="bbq-dish-lightbox-close"
        aria-label="Fechar"
        className="absolute top-5 right-5 sm:top-8 sm:right-8 h-11 w-11 rounded-full border border-[#F5EDD9]/25 hover:border-[#E85A1F] text-[#F5EDD9] hover:text-[#E85A1F] flex items-center justify-center transition-colors duration-300 z-10"
      >
        <X className="h-5 w-5" strokeWidth={1.5} />
      </button>

      {/* Prev / Next (desktop) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Anterior"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-[#F5EDD9]/25 hover:border-[#E85A1F] text-[#F5EDD9] hover:text-[#E85A1F] items-center justify-center transition-colors duration-300 z-10 font-serif-bbq italic text-2xl pb-1"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Próximo"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-[#F5EDD9]/25 hover:border-[#E85A1F] text-[#F5EDD9] hover:text-[#E85A1F] items-center justify-center transition-colors duration-300 z-10 font-serif-bbq italic text-2xl pb-1"
      >
        ›
      </button>

      {/* Body */}
      <div
        className="relative w-full max-w-[1100px] mx-auto px-5 sm:px-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-[4/5] sm:aspect-[16/11] max-h-[75vh] overflow-hidden bg-[#1A1510] mx-auto">
          <img
            key={d.slug}
            src={d.img}
            alt={d.name}
            className="absolute inset-0 w-full h-full object-contain img-enhance"
          />
        </div>
        <div className="mt-6 flex items-end justify-between gap-6 text-[#F5EDD9]">
          <div>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#E85A1F] font-sans-bbq">
              {`${index + 1} / ${dishes.length}`}
            </span>
            <h3 className="mt-2 font-gateway-serif italic text-[#F5EDD9] text-3xl sm:text-4xl leading-tight tracking-tight">
              {d.name}
            </h3>
            <p className="mt-2 font-gateway-sans text-sm sm:text-base text-[#9A8F7D] leading-relaxed max-w-md">
              {d.desc}
            </p>
          </div>
          {d.price && (
            <span className="shrink-0 font-gateway-serif italic text-[#E85A1F] text-2xl sm:text-3xl">
              {d.price}
              <span className="text-[#E85A1F] ml-0.5">€</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default BBQDishes;
